import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { NeoLogService } from '@app/services/neo-log.service';

/**
 * Single-purpose cross-tab signal: "somebody logged out".
 *
 * Uses `BroadcastChannel` where available and a `localStorage` + `storage`
 * event fallback otherwise. Any tab that receives this signal reloads itself
 * — MSAL's shared localStorage has already been cleared by the signalling
 * tab, so a reload lands at the login flow.
 *
 * This is the ONLY cross-tab coordination Neo.One has. No leader election,
 * no session-ready broadcasts, no tenant-switch banners. MSAL's own
 * localStorage token cache handles cross-tab authentication; everything
 * else (tenant switches, login flow) runs independently per tab.
 */
@Injectable({ providedIn: 'root' })
export class CrossTabLogoutService implements OnDestroy {
  private static readonly CHANNEL_NAME = 'neo-one:logout';
  private static readonly STORAGE_KEY = '__xtab:logout';

  private readonly neoLogService = inject(NeoLogService);
  private readonly logoutSubject = new Subject<void>();
  private readonly channel: BroadcastChannel | null;
  private readonly storageListener: ((event: StorageEvent) => void) | null;

  /** Fires when another tab calls {@link signalLogout}. */
  public readonly logout$: Observable<void> = this.logoutSubject.asObservable();

  constructor() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CrossTabLogoutService.CHANNEL_NAME);
      this.channel.onmessage = () => this.logoutSubject.next();
      this.storageListener = null;
    } else {
      this.channel = null;
      this.storageListener = (event: StorageEvent) => {
        if (event.key === CrossTabLogoutService.STORAGE_KEY && event.newValue) {
          this.logoutSubject.next();
        }
      };
      window.addEventListener('storage', this.storageListener);
    }
    this.neoLogService.log('CrossTabLogoutService->constructor', {
      transport: this.channel ? 'BroadcastChannel' : 'localStorage'
    });
  }

  /**
   * Broadcast a logout to every other tab. Called from `HeaderComponent.logout`
   * before clearing local storage so peers can react before MSAL state is
   * torn down.
   */
  public signalLogout(): void {
    if (this.channel) {
      this.channel.postMessage('logout');
      return;
    }
    // storage-event fallback: write + remove so the value change fires. Other
    // tabs only react to the write (newValue truthy); the remove is a no-op
    // for our listener.
    try {
      localStorage.setItem(CrossTabLogoutService.STORAGE_KEY, String(Date.now()));
      localStorage.removeItem(CrossTabLogoutService.STORAGE_KEY);
    } catch (error) {
      this.neoLogService.warn('CrossTabLogoutService->signalLogout->localStorage failed', error);
    }
  }

  public dispose(): void {
    this.channel?.close();
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
    this.logoutSubject.complete();
  }

  public ngOnDestroy(): void {
    this.dispose();
  }
}
